import { AreaChart, CartesianGrid, XAxis, Area, YAxis } from "recharts"
import { Card, CardContent } from "./ui/card"
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "./ui/chart"
import { ClockAlert } from "lucide-react"
import { ReactNode, useEffect, useState } from "react"
import { DataService } from "../services/data.service"
import { SensorModel } from "../models/sensor.model"
import { getCurrentDate } from "../lib/utils"
import { DateTime } from "luxon"

export interface LinearChartCardProps {
    deploymentId: number
    sensor: string,
    timezone: string,
    rooms: string[],
    children: ReactNode
}

export function ChartCard(props: LinearChartCardProps) {
    const [sensorData, setSensorData] = useState<SensorModel>()
    const [chartConfig, setChartConfig] = useState<ChartConfig>()
    const [chartData, setChartData] = useState<any>()
    const [selectedRoom, setSelectedRoom] = useState<string>(props.rooms[0])
    const [errorMessage, setErrorMessage] = useState<string>()
    const [localDateOffset, setLocalDateOffset] = useState<number>(0)

    useEffect(() => {
        DataService.getSensorData(props.deploymentId, props.sensor, selectedRoom, getCurrentDate(localDateOffset, props.timezone))
            .then((rsp) => {
                setChartConfig({
                    value: {
                        label: rsp.units,
                        color: rsp.color
                    }
                })
                setChartData(rsp.chart_data[0].data.map(arr => {
                    return {
                        title: DateTime.fromISO(arr[0]).setZone(props.timezone).toFormat("HH:mm"),
                        value: arr[1]
                    }
                }))
                setSensorData(rsp)
            })
            .catch((e: any) => setErrorMessage(e.message))
    }, [props.deploymentId, props.sensor, props.timezone, selectedRoom, localDateOffset])

    return (
        <Card className="p-4 shadow-md rounded-lg border" id="activity_report">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">{props.children}</h3>
            </div>

            {errorMessage != undefined ? (
                <div className="text-center text-red-500">
                    <p>Error: {errorMessage}</p>
                </div>
            ) : (sensorData == undefined || chartConfig == undefined || chartData == undefined) ? (
                <div className="text-center text-gray-600">Loading chart data... Please wait!</div>
            ) : (
                <div>
                    <div className="space-y-4 border-b pb-4 mb-4">
                        <div className="flex flex-wrap justify-between items-center space-x-2">
                            <select
                                className="flex-grow w-full max-w-[45%] px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-800 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                value={selectedRoom}
                                onChange={(e) => setSelectedRoom(e.target.value)}
                            >
                                {props.rooms.map((room) => (
                                    <option key={room} value={room}>
                                        {room}
                                    </option>
                                ))}
                            </select>

                            <select
                                className="flex-grow w-full max-w-[45%] px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-800 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                onChange={(e) => setLocalDateOffset(Number(e.target.value))}
                            >
                                {Array.from({ length: 30 }, (_, i) => {
                                    const offset = -1 * i;
                                    return (
                                        <option key={i} value={offset}>
                                            {offset === 0 ? 'Now' : getCurrentDate(offset, props.timezone)}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        <div className="flex items-center bg-gray-100 p-2 rounded-lg space-x-3">
                            <div className="bg-gray-100 p-2 rounded-lg">
                                <ClockAlert className="text-gray-800" />
                            </div>

                            <div className="flex flex-col">
                                <p className="text-sm font-medium text-gray-800">
                                    {new Date(sensorData.last_report_at).toLocaleString()}
                                </p>
                                <span className="text-xs text-gray-500">Last report</span>
                            </div>
                        </div>
                    </div>
                    <CardContent className="p-0">
                        {sensorData.chart_data[0].data.length == 0 ?
                            <div className="text-center text-gray-600">We haven't found any data for the selected interval</div>
                            : <ChartContainer config={chartConfig}>
                                <AreaChart accessibilityLayer data={chartData} margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
                                    <CartesianGrid vertical={false} />
                                    <XAxis
                                        dataKey="title"
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                        angle={-60}
                                    />
                                    <YAxis tickLine={false} axisLine={false} tickMargin={10} domain={['dataMin', 'auto']} />
                                    <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
                                    <Area dataKey="value" type="natural" fill={sensorData.color} fillOpacity={0.4} stroke={sensorData.color} dot={{ r: 2 }} connectNulls={true} />
                                </AreaChart>
                            </ChartContainer>
                        }

                    </CardContent>
                </div>
            )}
        </Card>
    )
}